import React from 'react';

const Product = () => {
  return (
    <div className="product-page bg-neutral-900 text-neutral-300">
      {/* Navigation Bar */}
      <nav className="navbar">
        <div className="container max-w-5xl mx-auto flex justify-between items-center py-4">
          <div className="logo text-3xl text-accent">
            @note
          </div>
          <ul className="nav-links flex space-x-6">
            <li><a href="/demo" className="hover:text-accent" target='_blank'>Demo</a></li>
            <li><a href="#features" className="hover:text-accent">Features</a></li>
            <li><a href="#use-cases" className="hover:text-accent">Use Cases</a></li>
            {/* <li><a href="#pricing" className="hover:text-accent">Pricing</a></li> */}
          </ul>
        </div>
      </nav>

      <section id="hero" className="hero">
        <div className="max-w-5xl mx-auto px-4 xl:px-0 pt-24 lg:pt-32">
          <h1 className="font-semibold text-5xl md:text-6xl">
            <span className="text-accent ">@note:</span>  Capture Every Insight and Never Miss Out
          </h1>
          <div className="max-w-4xl">
            <p className="mt-5 text-neutral-400 text-lg">
              With information everywhere, organizing valuable insights can be challenging. Our product simplifies the way you capture, organize, and retrieve content, so you never miss a key idea. Instant bookmarking, an intuitive interface, and ample storage keep insights at your fingertips—ready whenever you need them. Discover a smarter, streamlined way to manage what matters.
            </p>
          </div>
          <div className='mt-10'><a href="/demo" className="btn-primary bg-accent p-3 pl-8 pr-8 font-semibold text-neutral-900"  target='_blank'>View demo</a></div>
        </div>
      </section>

      <section id="features" className="features max-w-6xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14 mx-auto">
        <h2 className="font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-center">
          Features
        </h2>
        <div className="mt-5 lg:mt-16 grid lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-3">
            <div className="grid sm:grid-cols-2 gap-8 md:gap-12">
              <div className="flex gap-x-5">
                <svg className="shrink-0 mt-1 size-6 text-accent" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="10" x="3" y="11" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4" /><line x1="8" x2="8" y1="16" y2="16" /><line x1="16" x2="16" y1="16" y2="16" /></svg>
                <div className="grow">
                  <h3 className="text-lg font-semibold text-accent">
                    Instant Bookmarking
                  </h3>
                  <p className="mt-1">
                    Found something worth saving? Just send any URL to <span className='text-accent font-semibold'>@note</span> and it’s saved instantly. With one quick step, your content is securely stored and ready whenever you need it.
                  </p>
                </div>
              </div>
              <div className="flex gap-x-5">
                <svg className="shrink-0 mt-1 size-6 text-accent" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 10v12" /><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z" /></svg>
                <div className="grow">
                  <h3 className="text-lg font-semibold text-accent">
                    Automatic Categorization
                  </h3>
                  <p className="mt-1">
                  <span className='text-accent font-semibold'>@note</span> organizes each saved item with relevant keywords, eliminating the need for manual tagging. Every piece of information is categorized automatically, so you can find exactly what you need in seconds.
                  </p>
                </div>
              </div>
              <div className="flex gap-x-5">
                <svg className="shrink-0 mt-1 size-6 text-accent" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg>
                <div className="grow">
                  <h3 className="text-lg font-semibold text-accent">
                    Intelligent Questions
                  </h3>
                  <p className="mt-1">
                    Each saved item includes questions that it answers, providing you with a quick way to gauge which content is most relevant to your needs.
                  </p>
                </div>
              </div>
              <div className="flex gap-x-5">
                <svg className=" text-accent shrink-0 mt-1 size-6 text-blue-600 dark:text-blue-500" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                <div className="grow">
                  <h3 className="text-lg font-semibold text-accent">
                    Powerful Search
                  </h3>
                  <p className="mt-1">
                    Locate saved content by searching keywords or questions. <span className='text-accent font-semibold'>@note</span>’s powerful search makes retrieving information a breeze, letting you focus on what matters most.
                  </p>
                </div>
              </div>
              <div className="flex gap-x-5">
                <svg className=" text-accent shrink-0 mt-1 size-6 text-blue-600 dark:text-blue-500" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                <div className="grow">
                  <h3 className="text-lg font-semibold text-accent">
                    Seamless Synchronization Across Devices
                  </h3>
                  <p className="mt-1">
                    With <span className='text-accent font-semibold'>@note</span> available on Windows, macOS, Android, and iOS, your saved articles, recipes, and travel notes are synchronized across all your devices. Start reading on your mobile and continue on your desktop, or pull up notes anywhere you go. <span className='text-accent font-semibold'>@note</span> keeps your knowledge library accessible, updated, and at your fingertips wherever you are.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="use-cases  max-w-6xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14 mx-auto">
        <h2 className="font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-center">
          Use cases
        </h2>

        <div className="flex flex-wrap items-center mt-20 text-left text-center">
          <div className="w-full md:w-3/5 lg:w-1/2 px-4">
            <img src="https://picsum.photos/400/240" alt="gem" className="inline-block shadow-lg border border-merino-400" />
          </div>
          <div className="w-full md:w-2/5 lg:w-1/2 px-4 text-center md:text-left lg:pl-12">
            <h3 className="use-case-header">
            Stay on Top of Technology Trends
            </h3>
            <p className="sm:text-lg mt-6">
            Perfect for tech enthusiasts and professionals, <span className='text-accent font-semibold'>@note</span> categorizes tech articles with keywords like "AI," "cybersecurity," "cloud computing," and more. With these organized tags, finding relevant information on specific tech topics is quick and easy.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center mt-20 text-left text-center">
          <div className="w-full md:w-3/5 lg:w-1/2 px-4">
            <img src="https://picsum.photos/400/240" alt="project members" className="inline-block shadow-lg border border-merino-400" />
          </div>
          <div className="w-full md:w-2/5 lg:w-1/2 px-4 md:order-first text-center md:text-left lg:pr-12">
            <h3 className="use-case-header">
            Save and Find Recipes Easily
            </h3>
            <p className="sm:text-lg mt-6">
            <span className='text-accent font-semibold'>@note</span> is also ideal for organizing recipes. Save recipes with a single click and find them later by searching for specific ingredients or by cuisine, such as "Italian," "Mexican," or "gluten-free." With smart tagging, cooking inspiration is just a keyword away.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center mt-20 text-left  text-center">
          <div className="w-full md:w-3/5 lg:w-1/2 px-4">
            <img src="https://picsum.photos/400/240" alt="editor" className="inline-block shadow-lg border border-merino-400" />
          </div>
          <div className="w-full md:w-2/5 lg:w-1/2 px-4 text-center md:text-left lg:pl-12">
            <h3 className="use-case-header">
            Organize Travel Resources
            </h3>
            <p className="sm:text-lg mt-6">
            Planning a trip? Use <span className='text-accent font-semibold'>@note</span> to save travel articles, itineraries, and guides. Automatically categorized by destination ("Paris," "Japan") or activity type ("hiking," "museums," "family-friendly"), <span className='text-accent font-semibold'>@note</span> helps you keep travel resources organized. Search by keywords to pull up tips, itineraries, and destination highlights, whether you’re at home planning or on the go.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center mt-20 text-left text-center">
          <div className="w-full md:w-3/5 lg:w-1/2 px-4">
            <img src="https://picsum.photos/400/240" alt="bulk editing" className="inline-block shadow-lg border border-merino-400" />
          </div>
          <div className="w-full md:w-2/5 lg:w-1/2 px-4 md:order-first text-center md:text-left lg:pr-12">
            <h3 className="use-case-header">
            Build a Knowledge Library
            </h3>
            <p className="sm:text-lg mt-6">
            Whether for work, study, or personal growth, <span className='text-accent font-semibold'>@note</span> transforms your saved items into a searchable, structured knowledge library. Access insights whenever you need them and always stay informed.
            </p>
          </div>
        </div>

      </section>

      {/* CTA Section */}
      <section className="cta-section py-20">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-semibold mb-6">Get Started with <span className='text-accent font-semibold'>@note</span> Today</h2>
          <p className="text-lg mb-8">Experience the difference in how you manage and organize information.</p>
          <a href="/demo" className="btn-primary bg-accent p-3 pl-8 pr-8 font-semibold  text-neutral-900" target='_blank' >View demo</a>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer py-12">
        <div className="container mx-auto text-center">
          <p>© 2023 Tomasz Borycki. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Product;
